import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useApi } from "../lib/useApi";
import { ApiError, type Outage, type OutageAction, type OutageBooking } from "../lib/api";
import { usePermissions } from "../lib/roles";
import { Modal } from "../components/Modal";
import {
  MAX_DURATION_MINUTES,
  MIN_DURATION_MINUTES,
  STATE_LABEL,
  formatSydney,
  slotLabel,
  sydneyDate,
  validateWhen,
} from "../lib/outages";

function describe(e: unknown, what: string): string {
  if (e instanceof ApiError) {
    const detail = e.message.trim().replace(/^\d{3}\s*/, "");
    if (e.status === 400) return `${what} was refused as invalid (400)${detail ? `: ${detail}` : "."}`;
    if (e.status === 409) {
      return `${what} conflicts with another outage or the outage has already changed (409)${detail ? `: ${detail}` : "."}`;
    }
    if (e.status === 403) return `${what} was refused: this session lacks the cloud-team scope (403).`;
    return `${what} failed (${e.status})${detail ? `: ${detail}` : "."}`;
  }
  return `${what} failed: ${e}`;
}

/**
 * Which Restore control a row offers. Keyed on the takedown serving the outage, not on the outage's own state: a takedown
 * that is taking down or down offers Restore whatever the outage says (it may never have reached in_progress), one that is
 * restoring shows progress, and an outage in progress offers Restore whatever its takedown says (a restored takedown may sit over an outage another pod moved).
 */
function restoreControl(o: Outage, pressedHere: boolean): "restore" | "progress" | null {
  const td = o.takedown ?? null;
  if (td?.state === "restoring") return "progress";
  const restorable = o.state === "in_progress" || (!!td && (td.state === "taking_down" || td.state === "down"));
  if (!restorable) return null;
  return pressedHere ? "progress" : "restore";
}

/** Restore-the-silo is offered whenever a takedown serves the outage and is not restored, restoring included. */
function offersSiloRestore(o: Outage): boolean {
  return !!o.takedown && o.takedown.state !== "restored";
}

/** Whether a row this tab pressed Restore on still has a Restore under way, so the local mark is kept. */
function stillRestoring(o: Outage): boolean {
  return o.state === "in_progress" || (!!o.takedown && o.takedown.state !== "restored");
}

function Err({ msg, testId }: { msg: string; testId: string }) {
  return (
    <div className="callout error" role="alert" data-testid={testId}>
      <span className="mk">err</span>
      <span>{msg}</span>
    </div>
  );
}

export function ScheduledOutagesPage() {
  const api = useApi();
  const perms = usePermissions();

  const [outages, setOutages] = useState<Outage[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);
  const [confirmRestoreId, setConfirmRestoreId] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<Set<string>>(() => new Set());
  const restoreInFlight = useRef(false);
  const [confirmSiloId, setConfirmSiloId] = useState<string | null>(null);
  const siloInFlight = useRef(false);
  const [editing, setEditing] = useState<Outage | null>(null);
  const [defaultMessage, setDefaultMessage] = useState<string>("");
  const [defaultMessageError, setDefaultMessageError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setOutages(await api.listOutages());
    } catch (e) {
      setLoadError(describe(e, "Loading the outages"));
    }
  }, [api]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!perms.canManageOutages) return;
    let cancelled = false;
    api.getOutageDefaultMessage()
      .then((m) => { if (!cancelled) setDefaultMessage(m); })
      .catch((e) => { if (!cancelled) setDefaultMessageError(describe(e, "Loading the default message")); });
    return () => { cancelled = true; };
  }, [api, perms.canManageOutages]);

  const sorted = useMemo(
    () => (outages ? [...outages].sort((a, b) => a.start.localeCompare(b.start)) : null),
    [outages],
  );

  // While this tab pressed Restore on a row, or the server's takedown record for any row is taking_down or restoring or has
  // a silo restore under way (a reloaded tab, a second tab, another admin), poll every 2 s (first poll 2 s after the press or
  // the load); a row whose Restore has finished is dropped from the local set.
  const restoringKey = [...restoring].sort().join(",");
  const serverBusy = !!outages?.some((o) =>
    o.takedown?.state === "taking_down" || o.takedown?.state === "restoring" || (o.takedown?.siloRestore === "requested" && o.takedown?.state !== "restored"));
  useEffect(() => {
    if (!restoringKey && !serverBusy) return;
    const t = setInterval(() => {
      api.listOutages().then((list) => {
        setOutages(list);
        setRestoring((prev) => {
          const next = new Set([...prev].filter((id) => list.some((x) => x.id === id && stillRestoring(x))));
          return next.size === prev.size ? prev : next;
        });
      }).catch(() => { /* transient: the next tick retries */ });
    }, 2000);
    return () => clearInterval(t);
  }, [api, restoringKey, serverBusy]);

  async function restore(o: Outage) {
    if (restoreInFlight.current) return;
    restoreInFlight.current = true;
    setActionError(null);
    setConfirmRestoreId(null);
    setBusyId(o.id);
    let ok = false;
    try {
      await api.restoreOutage(o.id);
      ok = true;
      setRestoring((prev) => new Set(prev).add(o.id));
    } catch (e) {
      setActionError(describe(e, "Restore"));
    } finally {
      setBusyId(null);
      restoreInFlight.current = false;
      if (!ok) await load();
    }
  }

  async function restoreSilo(o: Outage) {
    if (siloInFlight.current) return;
    siloInFlight.current = true;
    setActionError(null);
    setConfirmSiloId(null);
    setBusyId(o.id);
    try {
      await api.restoreSilo(o.id);
    } catch (e) {
      setActionError(describe(e, "Restore silo"));
    } finally {
      setBusyId(null);
      siloInFlight.current = false;
      await load(); // the takedown's siloRestore as the server now holds it; the poll follows it while requested
    }
  }

  async function act(o: Outage, action: OutageAction) {
    setActionError(null);
    setConfirmCancelId(null);
    setBusyId(o.id);
    try {
      await api.outageAction(o.id, action);
    } catch (e) {
      setActionError(describe(e, `${action[0].toUpperCase()}${action.slice(1)}`));
    } finally {
      setBusyId(null);
      await load(); // success or a stale-row 409: show what the server now holds
    }
  }

  return (
    <>
      <div className="page-head reveal" data-d="1">
        <div className="crumb">Operations · Scheduled Outages</div>
        <h1>Scheduled Outages</h1>
        <p>
          Maintenance windows for the whole product. Every time on this page is Australia/Sydney time, whatever
          this browser's zone. An outage reaches users only while it is enabled.
        </p>
      </div>

      {perms.canManageOutages && (
        <BookingForm
          defaultMessage={defaultMessage}
          defaultMessageError={defaultMessageError}
          onBooked={load}
        />
      )}

      <section className="panel reveal" data-d="3" data-testid="outage-list">
        <header>
          <span className="idx">{sorted ? sorted.length : "…"}</span> Outages
          <span className="hint">start and end in Sydney time</span>
          <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto" }} onClick={() => void load()} data-testid="outage-refresh">
            Refresh
          </button>
        </header>
        <div className="body tight">
          {loadError && <Err msg={loadError} testId="outage-error" />}
          {actionError && <Err msg={actionError} testId="outage-error" />}
          {sorted && sorted.length === 0 && <div className="faint" style={{ padding: 16 }} data-testid="outage-empty">No outages.</div>}
          {sorted && sorted.length > 0 && (
            <table className="grid">
              <thead>
                <tr>
                  <th>State</th>
                  <th>Enabled</th>
                  <th>Automate takedown</th>
                  <th>Start (Sydney time)</th>
                  <th>End (Sydney time)</th>
                  <th>Minutes</th>
                  <th>Message</th>
                  {perms.canManageOutages && <th className="actions">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sorted.map((o) => (
                  <tr key={o.id} data-testid="outage-row" data-outage-id={o.id} data-state={o.state} data-revision={o.revision}>
                    <td data-testid="outage-state">{STATE_LABEL[o.state] ?? o.state}</td>
                    <td data-testid="outage-enabled">{o.enabled ? "Enabled" : "Disabled"}</td>
                    <td data-testid="outage-automate" data-value={String(o.automateTakedown)}>{o.automateTakedown ? "Automated" : "Manual"}</td>
                    <td data-testid="outage-start" className="mono">{formatSydney(o.start)}</td>
                    <td data-testid="outage-end" className="mono">{formatSydney(o.end)}</td>
                    <td data-testid="outage-duration">{o.durationMinutes}</td>
                    <td data-testid="outage-message">{o.message}</td>
                    {perms.canManageOutages && (
                      <td className="actions">
                        {o.state === "scheduled" && (
                          <div className="btn-row">
                            <button className="btn btn-sm" disabled={busyId === o.id} onClick={() => setEditing(o)} data-testid="outage-edit">Edit</button>
                            {o.enabled ? (
                              <button className="btn btn-sm" disabled={busyId === o.id} onClick={() => void act(o, "disable")} data-testid="outage-disable">Disable</button>
                            ) : (
                              <button className="btn btn-sm" disabled={busyId === o.id} onClick={() => void act(o, "enable")} data-testid="outage-enable">Enable</button>
                            )}
                            {confirmCancelId === o.id ? (
                              <>
                                <button className="btn btn-sm btn-danger" disabled={busyId === o.id} onClick={() => void act(o, "cancel")} data-testid="outage-cancel-confirm">Confirm cancel</button>
                                <button className="btn btn-ghost btn-sm" onClick={() => setConfirmCancelId(null)} data-testid="outage-cancel-keep">Keep</button>
                              </>
                            ) : (
                              <button className="btn btn-sm btn-danger" disabled={busyId === o.id} onClick={() => setConfirmCancelId(o.id)} data-testid="outage-cancel">Cancel</button>
                            )}
                          </div>
                        )}
                        <RestoreControls
                          o={o}
                          control={restoreControl(o, restoring.has(o.id))}
                          busy={busyId === o.id}
                          confirmRestore={confirmRestoreId === o.id}
                          onAskRestore={() => setConfirmRestoreId(o.id)}
                          onKeepRestore={() => setConfirmRestoreId(null)}
                          onRestore={() => void restore(o)}
                          confirmSilo={confirmSiloId === o.id}
                          onAskSilo={() => setConfirmSiloId(o.id)}
                          onKeepSilo={() => setConfirmSiloId(null)}
                          onRestoreSilo={() => void restoreSilo(o)}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!sorted && !loadError && <div className="faint" style={{ padding: 16 }}>Loading…</div>}
        </div>
      </section>

      {editing && (
        <Modal title="Edit outage" onClose={() => setEditing(null)}>
          <EditForm
            outage={editing}
            onDone={async () => { setEditing(null); await load(); }}
            onStale={async () => { await load(); }}
          />
        </Modal>
      )}
    </>
  );
}

/**
 * The Restore and restore-the-silo controls of one row, in one btn-row: Restore (or its progress), then Restore silo with
 * the silo's progress, then the takedown's state.
 */
function RestoreControls({
  o, control, busy, confirmRestore, onAskRestore, onKeepRestore, onRestore, confirmSilo, onAskSilo, onKeepSilo, onRestoreSilo,
}: {
  o: Outage;
  control: "restore" | "progress" | null;
  busy: boolean;
  confirmRestore: boolean;
  onAskRestore: () => void;
  onKeepRestore: () => void;
  onRestore: () => void;
  confirmSilo: boolean;
  onAskSilo: () => void;
  onKeepSilo: () => void;
  onRestoreSilo: () => void;
}) {
  const td = o.takedown ?? null;
  const silo = offersSiloRestore(o);
  if (!control && !silo) return null;
  return (
    <div className="btn-row">
      {control === "progress" && (
        <span className="faint" role="status" data-testid="restore-progress">
          {td ? `Restoring… ${td.stepsRestored}/${td.stepsTotal}` : "Restoring…"}
        </span>
      )}
      {control === "restore" && (confirmRestore ? (
        <>
          <button className="btn btn-sm btn-danger" disabled={busy} onClick={onRestore} data-testid="outage-restore-confirm">Confirm restore</button>
          <button className="btn btn-ghost btn-sm" disabled={busy} onClick={onKeepRestore} data-testid="outage-restore-keep">Keep</button>
        </>
      ) : (
        <button className="btn btn-sm" disabled={busy} onClick={onAskRestore} data-testid="outage-restore">Restore</button>
      ))}
      {silo && (confirmSilo ? (
        <>
          <button className="btn btn-sm btn-danger" disabled={busy} onClick={onRestoreSilo} data-testid="outage-restore-silo-confirm">Confirm restore silo</button>
          <button className="btn btn-ghost btn-sm" disabled={busy} onClick={onKeepSilo} data-testid="outage-restore-silo-keep">Keep</button>
        </>
      ) : (
        <button className="btn btn-sm" disabled={busy} onClick={onAskSilo} data-testid="outage-restore-silo">Restore silo</button>
      ))}
      {td?.siloRestore === "requested" && <span className="faint" role="status" data-testid="restore-silo-progress">Restoring the silo…</span>}
      {td?.siloRestore === "restored" && <span className="faint" data-testid="outage-silo-state">Silo: restored</span>}
      {td && control === "restore" && <span className="faint" data-testid="outage-takedown-state">Takedown: {td.state}</span>}
    </div>
  );
}

interface FormFields {
  date: string;
  duration: string;
  message: string;
  automate: boolean;
}

function WhenFields({ f, set, idPrefix, keptStart, onUseRuleSlot }: {
  f: FormFields;
  set: (p: Partial<FormFields>) => void;
  idPrefix: string;
  /** Edit only: the outage's own start (ISO), kept while set; the slot rule is not applied. */
  keptStart?: string;
  onUseRuleSlot?: () => void;
}) {
  const n = /^\d+$/.test(f.duration.trim()) ? Number(f.duration) : NaN;
  return (
    <>
      <div className="outage-form-grid">
        <div className="field">
          <label htmlFor={`${idPrefix}-date`}>Date (Sydney)</label>
          <input id={`${idPrefix}-date`} type="date" value={f.date} onChange={(e) => set({ date: e.target.value })} data-testid={`${idPrefix}-date`} />
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-duration`}>Duration (minutes)</label>
          <input
            id={`${idPrefix}-duration`}
            type="number"
            step={1}
            min={MIN_DURATION_MINUTES}
            max={MAX_DURATION_MINUTES}
            value={f.duration}
            onChange={(e) => set({ duration: e.target.value })}
            data-testid={`${idPrefix}-duration`}
          />
          <div className="hint">{MIN_DURATION_MINUTES} to {MAX_DURATION_MINUTES}</div>
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-message`}>Message</label>
          <textarea id={`${idPrefix}-message`} rows={3} value={f.message} onChange={(e) => set({ message: e.target.value })} data-testid={`${idPrefix}-message`} />
        </div>
      </div>
      <div className="outage-slot" data-testid={`${idPrefix}-slot`}>
        {keptStart !== undefined
          ? (
            <>
              {`Keeps the booked start: ${formatSydney(keptStart)}`}
              {Number.isFinite(n) && n > 0 ? `, ends ${formatSydney(new Date(Date.parse(keptStart) + n * 60_000).toISOString())}` : ""}
              {onUseRuleSlot && (
                <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: 8 }} onClick={onUseRuleSlot} data-testid={`${idPrefix}-use-slot`}>
                  Use the rule slot for {f.date} instead
                </button>
              )}
            </>
          )
          : Number.isFinite(n) && n > 0
          ? `Slot: starts ${slotLabel(n)}${f.date ? ` on ${f.date}` : ""}`
          : "Slot: enter a duration to see the start time"}
      </div>
    </>
  );
}

function BookingForm({ defaultMessage, defaultMessageError, onBooked }: {
  defaultMessage: string;
  defaultMessageError: string | null;
  onBooked: () => Promise<void>;
}) {
  const api = useApi();
  const [f, setF] = useState<FormFields>({ date: "", duration: "60", message: "", automate: false });
  const [enableNow, setEnableNow] = useState(false);
  const [messageTouched, setMessageTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Prefill from the API default once it arrives, unless the admin has already typed.
  useEffect(() => {
    if (!messageTouched && defaultMessage) setF((p) => ({ ...p, message: defaultMessage }));
  }, [defaultMessage, messageTouched]);

  function set(p: Partial<FormFields>) {
    if (p.message !== undefined) setMessageTouched(true);
    setF((prev) => ({ ...prev, ...p }));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const bad = validateWhen(f.date, f.duration) ?? (f.message.trim() ? null : "Enter a message.");
    if (bad) { setError(bad); return; }
    const body: OutageBooking = {
      date: f.date,
      durationMinutes: Number(f.duration),
      message: f.message,
      automateTakedown: f.automate,
      enabled: enableNow,
    };
    setBusy(true);
    try {
      await api.bookOutage(body);
      setF({ date: "", duration: "60", message: defaultMessage, automate: false });
      setMessageTouched(false);
      setEnableNow(false);
      await onBooked();
    } catch (err) {
      setError(describe(err, "Booking"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel reveal" data-d="2" data-testid="book-panel">
      <header>
        <span className="idx">+</span> Book an outage
        <span className="hint">the server picks the start: 12:00 Sydney for up to 90 minutes, otherwise 19:00</span>
      </header>
      <div className="body">
        <form onSubmit={submit} noValidate data-testid="book-form">
          <WhenFields f={f} set={set} idPrefix="book" />
          {defaultMessageError && <div className="hint err" data-testid="book-default-error">{defaultMessageError}</div>}
          <div className="outage-checks">
            <label>
              <input type="checkbox" checked={f.automate} onChange={(e) => set({ automate: e.target.checked })} data-testid="book-automate" />
              Automate takedown
            </label>
            <label>
              <input type="checkbox" checked={enableNow} onChange={(e) => setEnableNow(e.target.checked)} data-testid="book-enable" />
              Enable on booking
            </label>
          </div>
          {error && <Err msg={error} testId="book-error" />}
          <div className="btn-row" style={{ marginTop: 12 }}>
            <button className={"btn btn-primary" + (busy ? " loading" : "")} disabled={busy} data-testid="book-submit">
              <span className="glyph" /> Book outage
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function EditForm({ outage, onDone, onStale }: { outage: Outage; onDone: () => Promise<void>; onStale: () => Promise<void> }) {
  const api = useApi();
  const [f, setF] = useState<FormFields>({
    date: outage.date ?? sydneyDate(outage.start),
    duration: String(outage.durationMinutes),
    message: outage.message,
    automate: outage.automateTakedown,
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // An outage booked by explicit start has no date: the edit keeps that start until the admin picks the rule slot,
  // by the button or by changing the date. The choice is explicit state, so changing the date back does not re-arm it.
  const [useRuleSlot, setUseRuleSlot] = useState(outage.date !== null);
  const keepStart = !useRuleSlot;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const bad = validateWhen(f.date, f.duration) ?? (f.message.trim() ? null : "Enter a message.");
    if (bad) { setError(bad); return; }
    setBusy(true);
    try {
      await api.updateOutage(outage.id, {
        ...(keepStart ? { start: outage.start } : { date: f.date }),
        durationMinutes: Number(f.duration),
        message: f.message,
        automateTakedown: f.automate,
      });
      await onDone();
    } catch (err) {
      setError(describe(err, "Saving the edit"));
      await onStale();
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate data-testid="edit-form">
      <WhenFields
        f={f}
        set={(p) => { if (p.date !== undefined) setUseRuleSlot(true); setF((prev) => ({ ...prev, ...p })); }}
        idPrefix="edit"
        keptStart={keepStart ? outage.start : undefined}
        onUseRuleSlot={() => setUseRuleSlot(true)}
      />
      <div className="outage-checks">
        <label>
          <input type="checkbox" checked={f.automate} onChange={(e) => setF((p) => ({ ...p, automate: e.target.checked }))} data-testid="edit-automate" />
          Automate takedown
        </label>
      </div>
      {error && <Err msg={error} testId="edit-error" />}
      <div className="btn-row" style={{ marginTop: 12 }}>
        <button className={"btn btn-primary" + (busy ? " loading" : "")} disabled={busy} data-testid="edit-submit">
          <span className="glyph" /> Save
        </button>
      </div>
    </form>
  );
}
